import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  A status mark, not a coloured pill.

  The old version used Tailwind's stock scale (`bg-green-50 text-green-700
  ring-green-600/20`) and a `rounded-full` shape. Two problems: the stock scale
  has no dark counterpart, so a pale green chip kept its pale green ground on a
  near-black card and took its own text with it; and a full pill reads as
  something dismissible rather than as a state the record is in.

  Five tones, each a 7% wash under a 25% hairline with the tone's own ink —
  the same construction as every other status surface in the product.
*/

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

const tones: Record<Tone, string> = {
  success: styles.markSuccess,
  warning: styles.markWarning,
  danger: styles.markDanger,
  info: styles.markInfo,
  neutral: styles.markNeutral,
};

export interface MarkProps {
  tone?: Tone;
  /** A 5px dot before the label — for a live state that is being watched. */
  dot?: boolean;
  children: ReactNode;
  className?: string;
}

export function Mark({ tone = 'neutral', dot, children, className }: MarkProps) {
  return (
    <span className={cn(tones[tone], className)}>
      {dot && <span className="h-[5px] w-[5px] rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/* ── Compatibility shim ─────────────────────────────────────────────────────
   Colour names (`green`, `amber`, `red`…) describe paint, not meaning, so new
   code should use <Mark tone="success">. These map the old names onto the new
   tones so existing call sites keep working while they are migrated. */

type LegacyVariant = 'green' | 'amber' | 'red' | 'blue' | 'slate';

const legacy: Record<LegacyVariant, Tone> = {
  green: 'success',
  amber: 'warning',
  red: 'danger',
  blue: 'info',
  slate: 'neutral',
};

export function Badge({
  variant = 'slate',
  children,
  className,
}: {
  variant?: LegacyVariant;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Mark tone={legacy[variant]} className={className}>
      {children}
    </Mark>
  );
}

/* ── Domain mappings ────────────────────────────────────────────────────────
   Kept here rather than in each page so one pickup event type cannot be
   "Released / green" on the board and "released / grey" in the audit log. */

const EVENT_TONES: Record<string, { label: string; tone: Tone }> = {
  released: { label: 'Released', tone: 'success' },
  failed_attempt: { label: 'Failed', tone: 'danger' },
  suspicious_flagged: { label: 'Flagged', tone: 'danger' },
  code_revoked: { label: 'Revoked', tone: 'warning' },
  delegate_added: { label: 'Delegate added', tone: 'info' },
  delegate_cancelled: { label: 'Delegate cancelled', tone: 'warning' },
  offline_sync: { label: 'Offline sync', tone: 'neutral' },
};

export function eventMark(eventType: string) {
  const entry = EVENT_TONES[eventType] ?? { label: eventType.replace(/_/g, ' '), tone: 'neutral' as Tone };
  return <Mark tone={entry.tone}>{entry.label}</Mark>;
}

/** @deprecated use {@link eventMark} */
export const statusBadge = eventMark;

const STUDENT_TONES: Record<string, Tone> = {
  active: 'success',
  inactive: 'neutral',
  graduated: 'info',
  suspended: 'warning',
  withdrawn: 'danger',
};

export function studentStatusTone(status: string): Tone {
  return STUDENT_TONES[status] ?? 'neutral';
}
